"use client";

import React, { Fragment } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";
import logo from "@/public/logo-black.svg";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { PageAffiliateWorkshopSchema } from "@/sanity/pages/PageAffiliateWorkshopTemplate";
import { cn } from "@/lib/utils";
import Countdown from "@/components/global/countdown/Countdown";
import Link from "next/link";
import arrow from "@/public/global/dotted-arrow.svg";

export default function FomoSection({
  data,
}: {
  data: PageAffiliateWorkshopSchema;
}) {
  return (
    <section
      id="fomo"
      className="w-full h-full lg:px-[5rem] px-[2rem] pt-[5rem]"
    >
      <div className="relative mx-auto w-[min(230rem,100%)] bg-[#07171B] rounded-[5.5rem] py-[8rem] lg:py-[17.75rem]">
        <div className="relative mx-auto w-full z-[1] flex flex-col items-center px-[2rem] lg:px-0">
          {data?.fomoChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <ChipSerif text={data.fomoChip} className="mb-0 text-white/70" />
            </motion.div>
          )}
          <motion.div
            className="richtext richtext-center prose-headings:text-white prose-p:text-white/70 prose-p:text-[1.75rem] lg:prose-p:text-[2rem] prose-p:w-[min(66.625rem,100%)]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {data?.fomoTitle && (
              <PortableText
                value={data?.fomoTitle}
                components={richTextComponent}
              />
            )}
          </motion.div>
          {data.toggleCountdown && data.countdown && (
            <div className="w-[min(104rem,100%)] my-[5rem] bg-[#6DA991] rounded-[1.5rem] lg:rounded-[2.25rem] border border-[#10303729] prose-h2:text-[#103037] prose-h2:lg:text-[9.5rem] prose-h6:text-[#103037]/70">
              <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-[2rem] py-[3rem] px-[2rem] lg:px-[5.5rem] border-b border-[#103037]/10">
                <motion.div
                  className="flex flex-col lg:flex-row gap-[0.75rem] justify-center items-center space-x-[1rem] w-full lg:w-fit"
                  initial={{ opacity: 0, filter: "blur(10px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  transition={{
                    duration: 0.3,
                    delay: 0,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                >
                  <Link href={"/"} className="shrink-0">
                    <Image src={logo} alt="" className="!w-[15rem]" />
                  </Link>

                  {data?.heroWorkshopTitle && (
                    <Fragment>
                      <Image
                        src={arrow}
                        alt=""
                        className="shrink-0 !w-[3.182rem] rotate-90 lg:rotate-0"
                      />
                      <div className="flex items-center gap-[1rem]">
                        <p className="text-[#103037] text-[2rem] font-[400] leading-[116%] m-0 lg:w-auto">
                          {data?.heroWorkshopTitle}
                        </p>
                      </div>
                    </Fragment>
                  )}
                </motion.div>

                <div className="flex">
                  <span className="text-[#103037]/60 text-[2rem]">
                    {data.dates.start}
                    <span className="text-[#103037]"> / {data.dates.end}</span>
                  </span>
                </div>
              </div>
              <Countdown
                dateTimeString={data.countdown}
                lang={data.countdownTextLang ?? "de"}
              />
            </div>
          )}
          <motion.div
            className="richtext richtext-center prose-headings:text-white prose-p:text-white/70 prose-p:text-[2rem] lg:prose-p:text-[2.25rem] prose-p:w-[min(81.25rem,90%)] prose-strong:text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {data?.fomoText && (
              <PortableText
                value={data?.fomoText}
                components={richTextComponent}
              />
            )}
          </motion.div>

          <div>
            <CTAButton
              data={data.fomoCTA}
              className="w-full lg:w-fit mt-[5rem]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
